import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

interface ImageCropperProps {
  imageSrc: string;
  onCropComplete: (croppedImageBlob: Blob) => void;
  onCancel: () => void;
  aspectRatio?: number;
  onCropRequest?: () => void;
  triggerCrop?: boolean;
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
  isPoster = false
) {
  if (isPoster || aspect === 1) {
    const imageAspect = mediaWidth / mediaHeight;
    let cropWidth: number;

    if (imageAspect >= 1) {
      cropWidth = (mediaHeight / mediaWidth) * 100;
    } else {
      cropWidth = 100;
    }

    return centerCrop(
      makeAspectCrop(
        {
          unit: "%" as const,
          width: cropWidth,
        },
        aspect,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  }

  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 100,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  imageSrc,
  onCropComplete,
  onCancel,
  aspectRatio = 16 / 9,
  onCropRequest,
  triggerCrop,
}) => {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    setCrop(undefined);
    setCompletedCrop(undefined);
  }, [imageSrc]);

  useEffect(() => {
    if (onCropRequest) {
      onCropRequest();
    }
  }, [onCropRequest]);

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;
      // const isPoster =
      //   !aspectRatio ||
      //   aspectRatio === 0 ||
      //   aspectRatio === 1 ||
      //   Math.abs(aspectRatio - 1) < 0.01;
      const isPoster = aspectRatio === 0;

      if (isPoster) {
        const crop = {
          unit: "%" as const,
          x: 0,
          y: 0,
          width: 100,
          height: 100,
        };
        // const initialCrop = centerAspectCrop(width, height, aspectRatio, true);
        setCrop(crop);
        requestAnimationFrame(() => {
          setCrop(crop);
        });
      } else {
        const initialCrop = centerAspectCrop(width, height, aspectRatio, false);
        setCrop(initialCrop);
        requestAnimationFrame(() => {
          setCrop(initialCrop);
        });
      }
    },
    [aspectRatio]
  );

  const getCroppedImg = useCallback(async (): Promise<Blob | null> => {
    if (!imgRef.current || !completedCrop) {
      return null;
    }

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return null;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        "image/jpeg",
        0.9
      );
    });
  }, [completedCrop]);

  const handleCropComplete = useCallback(async () => {
    if (!completedCrop) {
      alert("Please select a crop area first");
      return;
    }

    setIsProcessing(true);
    try {
      const croppedBlob = await getCroppedImg();
      if (croppedBlob) {
        onCropComplete(croppedBlob);
      } else {
        alert("Failed to crop image. Please try again.");
      }
    } catch (error) {
      console.error("Crop error:", error);
      alert("Error cropping image. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  }, [completedCrop, getCroppedImg, onCropComplete]);

  useEffect(() => {
    if (triggerCrop && completedCrop) {
      handleCropComplete();
    }
  }, [triggerCrop, completedCrop, handleCropComplete]);

  return (
    <div className="d-flex flex-column gap-3">
      <div className="text-center">
        <h6>Crop Image</h6>
        <p className="text-muted">
          Adjust the crop area to your desired thumbnail (Aspect Ratio:{" "}
          {aspectRatio === 1
            ? "1:1"
            : aspectRatio === 16 / 9
              ? "16:9"
              : aspectRatio === 9 / 16
                ? "9:16"
                : aspectRatio === 4 / 3
                  ? "4:3"
                  : aspectRatio === 3 / 2
                    ? "3:2"
                    : "Free"}
          )
        </p>
      </div>

      <div className="d-flex justify-content-center">
        <div style={{ maxWidth: "100%", maxHeight: "400px", overflow: "auto" }}>
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => {
              setCrop(percentCrop);
            }}
            onComplete={(c) => {
              setCompletedCrop(c);
            }}
            aspect={aspectRatio && aspectRatio !== 0 ? aspectRatio : undefined}
            minWidth={0}
            minHeight={0}
            keepSelection
          >
            <img
              ref={imgRef}
              alt="Crop me"
              src={imageSrc}
              style={{ maxWidth: "100%", maxHeight: "400px" }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        </div>
      </div>
      <div className="d-flex justify-content-center gap-2 H">
        <button
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={isProcessing}
        >
          Cancel
        </button>
      </div>
      {completedCrop && (
        <div className="text-center text-muted small">
          <p>
            Crop size: {Math.round(completedCrop.width)} ×{" "}
            {Math.round(completedCrop.height)} pixels
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageCropper;
